import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Shield, 
  Download, 
  Trash2, 
  Eye, 
  FileText, 
  Mail, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  User,
  Database,
  Lock,
  Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import gdprService, { GDPRRequest, UserDataSummary, AuthenticatedGDPRRequestData } from '../services/gdprService';



const GDPRCompliancePage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'requests' | 'data' | 'delete'>('overview');
  const [requests, setRequests] = useState<GDPRRequest[]>([]);
  const [dataSummary, setDataSummary] = useState<UserDataSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [requestForm, setRequestForm] = useState({
    type: 'access',
    description: ''
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  useEffect(() => {
    if (user) {
      loadMyRequests();
      loadDataSummary();
    }
  }, [user]);

  const loadMyRequests = async () => {
    try {
      const data = await gdprService.getMyRequests();
      setRequests(data);
    } catch (error) {
      console.error('Failed to load GDPR requests:', error);
      toast.error('Failed to load your GDPR requests');
    }
  };

  const loadDataSummary = async () => {
    try {
      const data = await gdprService.getMyDataSummary();
      setDataSummary(data);
    } catch (error) {
      console.error('Failed to load data summary:', error);
      toast.error('Failed to load your data summary');
    }
  };

  const submitGDPRRequest = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const requestData: AuthenticatedGDPRRequestData = {
        type: requestForm.type,
        description: requestForm.description || undefined
      };

      const response = await gdprService.submitAuthenticatedGDPRRequest(requestData);

      if (response.success) {
        toast.success(response.message || 'GDPR request submitted successfully.');
        setRequestForm({ type: 'access', description: '' });
        loadMyRequests();
      } else {
        toast.error('Failed to submit GDPR request');
      }
    } catch (error: any) {
      console.error('GDPR request submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit GDPR request');
    } finally {
      setLoading(false);
    }
  };

  const exportMyData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await gdprService.exportMyData();

      if (response.success) {
        toast.success(response.message || 'Data export initiated. You will receive a download link shortly.');
        loadMyRequests();
      } else {
        toast.error('Failed to initiate data export');
      }
    } catch (error: any) {
      console.error('Data export error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate data export');
    } finally {
      setLoading(false);
    }
  };

  const deleteMyAccount = async () => {
    if (!user || deleteConfirmation !== 'DELETE') return;
    
    setLoading(true);
    try {
      const response = await gdprService.deleteMyAccount();

      if (response.success) {
        toast.success(response.message || 'Account deletion initiated. You will be logged out shortly.');
        // Redirect to logout or home page
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      } else {
        toast.error('Failed to delete account');
      }
    } catch (error: any) {
      console.error('Account deletion error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Modern Header */}
      <header className="backdrop-blur-2xl bg-white/80 dark:bg-slate-900/80 border-b border-slate-200/50 dark:border-slate-700/30 shadow-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <Link to="/app/dashboard" className="group">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-purple-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/25 transition-all duration-300 group-hover:scale-105">
                  <Shield className="w-6 h-6 text-white" />
                </div>
              </Link>
              <div>
                <h1 className="text-3xl font-black bg-gradient-to-r from-slate-900 via-blue-600 to-purple-600 dark:from-white dark:via-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
                  Privacy & Compliance
                </h1>
                <p className="text-slate-600 dark:text-slate-300 text-sm mt-1">
                  Manage your data rights and privacy preferences
                </p>
              </div>
            </div>
            <Link to="/app/dashboard">
              <Button variant="outline" className="bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm border-slate-300/50 dark:border-slate-600/50 hover:bg-slate-100/50 dark:hover:bg-slate-700/50">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Modern Navigation Tabs */}
        <div className="flex space-x-2 bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl p-2 rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-lg mb-8">
          {[
            { id: 'overview', label: 'Overview', icon: Shield, color: 'blue' },
            { id: 'requests', label: 'My Requests', icon: FileText, color: 'purple' },
            { id: 'data', label: 'My Data', icon: Database, color: 'green' },
            { id: 'delete', label: 'Delete Account', icon: Trash2, color: 'red' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${
                activeTab === tab.id
                  ? `bg-gradient-to-r ${
                      tab.color === 'blue' ? 'from-blue-500 to-blue-600' :
                      tab.color === 'purple' ? 'from-purple-500 to-purple-600' :
                      tab.color === 'green' ? 'from-green-500 to-green-600' :
                      'from-red-500 to-red-600'
                    } text-white shadow-lg shadow-${tab.color}-500/25 scale-105`
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/50 dark:hover:bg-slate-700/50'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/80 to-white/40 dark:from-slate-800/80 dark:to-slate-800/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-indigo-500/5"></div>
              <div className="relative p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg mr-4">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Your Privacy Rights</h2>
                    <p className="text-slate-600 dark:text-slate-300 mt-1">
                      Under GDPR and other privacy regulations, you have comprehensive rights regarding your personal data.
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    { icon: Eye, title: 'Right to Access', desc: 'Request a copy of all personal data we hold about you.', color: 'blue' },
                    { icon: Download, title: 'Data Portability', desc: 'Export your data in a machine-readable format.', color: 'green' },
                    { icon: Trash2, title: 'Right to Erasure', desc: 'Request deletion of your personal data ("right to be forgotten").', color: 'red' },
                    { icon: Lock, title: 'Restrict Processing', desc: 'Limit how we process your personal data.', color: 'purple' },
                    { icon: FileText, title: 'Rectification', desc: 'Correct inaccurate or incomplete personal data.', color: 'orange' },
                    { icon: AlertTriangle, title: 'Object to Processing', desc: 'Object to certain types of data processing.', color: 'yellow' }
                  ].map((right, index) => (
                    <div key={index} className="group p-6 bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 hover:shadow-lg transition-all duration-300 hover:scale-105">
                      <div className={`w-10 h-10 bg-gradient-to-br ${
                        right.color === 'blue' ? 'from-blue-500 to-blue-600' :
                        right.color === 'green' ? 'from-green-500 to-green-600' :
                        right.color === 'red' ? 'from-red-500 to-red-600' :
                        right.color === 'purple' ? 'from-purple-500 to-purple-600' :
                        right.color === 'orange' ? 'from-orange-500 to-orange-600' :
                        'from-yellow-500 to-yellow-600'
                      } rounded-xl flex items-center justify-center mb-4 shadow-lg`}>
                        <right.icon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-bold text-slate-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {right.title}
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                        {right.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/80 to-white/40 dark:from-slate-800/80 dark:to-slate-800/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-blue-500/5 to-purple-500/5"></div>
              <div className="relative p-8">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg mr-4">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">Quick Actions</h2>
                    <p className="text-slate-600 dark:text-slate-300 text-sm">
                      Common privacy-related actions you can take right now.
                    </p>
                  </div>
                </div>
                
                {/* Data Controller Information */}
                <div className="mt-8 p-6 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl border-2 border-indigo-200 dark:border-indigo-800">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Data Controller</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
                        The person/entity responsible for determining how your data is processed:
                      </p>
                      <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-indigo-200 dark:border-indigo-700">
                        <p className="font-semibold text-lg text-slate-900 dark:text-white mb-1">
                          Quixocial
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          This individual/entity controls all Platform Data shared with us by Meta (Facebook, Instagram) and other social media platforms, and is responsible for ensuring compliance with GDPR, CCPA, and other applicable privacy regulations.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                  <Button 
                    onClick={exportMyData} 
                    disabled={loading} 
                    className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg shadow-blue-500/25 border-0 rounded-xl py-6 transition-all duration-300 hover:scale-105"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Export My Data
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setActiveTab('requests')}
                    className="bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm border-slate-300/50 dark:border-slate-600/50 hover:bg-slate-100/50 dark:hover:bg-slate-700/50 rounded-xl py-6 transition-all duration-300 hover:scale-105"
                  >
                    <FileText className="w-5 h-5 mr-2" />
                    Submit Request
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setActiveTab('data')}
                    className="bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm border-slate-300/50 dark:border-slate-600/50 hover:bg-slate-100/50 dark:hover:bg-slate-700/50 rounded-xl py-6 transition-all duration-300 hover:scale-105"
                  >
                    <Eye className="w-5 h-5 mr-2" />
                    View My Data
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <div className="space-y-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/80 to-white/40 dark:from-slate-800/80 dark:to-slate-800/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-blue-500/5 to-indigo-500/5"></div>
              <div className="relative p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg mr-4">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Submit New GDPR Request</h2>
                    <p className="text-slate-600 dark:text-slate-300 mt-1">
                      Submit a formal request regarding your personal data.
                    </p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">
                      Request Type
                    </label>
                    <select
                      value={requestForm.type}
                      onChange={(e) => setRequestForm({ ...requestForm, type: e.target.value })}
                      className="w-full px-4 py-3 bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 dark:focus:border-purple-400 transition-all duration-200 text-slate-900 dark:text-white shadow-sm hover:shadow-md backdrop-blur-sm"
                    >
                      <option value="access">Data Access Request</option>
                      <option value="portability">Data Portability Request</option>
                      <option value="rectification">Data Rectification Request</option>
                      <option value="erasure">Data Erasure Request</option>
                      <option value="restrict">Restrict Processing Request</option>
                      <option value="object">Object to Processing Request</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">
                      Description (Optional)
                    </label>
                    <Textarea
                      value={requestForm.description}
                      onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
                      placeholder="Provide additional details about your request..."
                      rows={4}
                      className="w-full px-4 py-3 bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 dark:focus:border-purple-400 transition-all duration-200 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 shadow-sm hover:shadow-md backdrop-blur-sm resize-none"
                    />
                  </div>
                  
                  <Button 
                    onClick={submitGDPRRequest} 
                    disabled={loading}
                    className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white shadow-lg shadow-purple-500/25 border-0 rounded-xl py-3 px-8 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {loading ? (
                      <>
                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Submit Request
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>My GDPR Requests</CardTitle>
                <CardDescription>
                  Track the status of your privacy requests.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {requests.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No GDPR requests found.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {requests.map((request) => (
                      <div key={request.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-900 dark:text-white capitalize">
                            {request.request_type.replace('_', ' ')} Request
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                            {request.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        
                        {request.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {request.description}
                          </p>
                        )}
                        
                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 space-x-4">
                          <span>Submitted: {new Date(request.created_at).toLocaleDateString()}</span>
                          <span>Due: {new Date(request.response_due_date).toLocaleDateString()}</span>
                        </div>
                        
                        {request.export_file_path && (
                          <div className="mt-3">
                            <Button size="sm" asChild>
                              <a href={`/api/v1/gdpr/download/${request.id}`} target="_blank" rel="noopener noreferrer">
                                <Download className="w-4 h-4 mr-2" />
                                Download Export
                              </a>
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Data Tab */}
        {activeTab === 'data' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>My Data Summary</CardTitle>
                <CardDescription>
                  Overview of the personal data we store about you.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dataSummary ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <User className="w-5 h-5 text-blue-600 mr-2" />
                        <h3 className="font-semibold text-gray-900 dark:text-white">Profile</h3>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Name: {dataSummary.user_info.first_name} {dataSummary.user_info.last_name}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Email: {dataSummary.user_info.email}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Member since: {new Date(dataSummary.user_info.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    
                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <FileText className="w-5 h-5 text-green-600 mr-2" />
                        <h3 className="font-semibold text-gray-900 dark:text-white">Content</h3>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Posts: {dataSummary.posts_count}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Media files: {dataSummary.media_files_count}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        AI tasks: {dataSummary.ai_tasks_count}
                      </p>
                    </div>
                    
                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Database className="w-5 h-5 text-purple-600 mr-2" />
                        <h3 className="font-semibold text-gray-900 dark:text-white">Usage</h3>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Clients: {dataSummary.clients_count}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Storage used: {formatBytes(dataSummary.total_storage_used)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    Loading data summary...
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Delete Tab */}
        {activeTab === 'delete' && (
          <div className="space-y-6">
            <Card className="border-red-200 dark:border-red-800">
              <CardHeader>
                <CardTitle className="text-red-600 dark:text-red-400 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  Delete My Account
                </CardTitle>
                <CardDescription>
                  Permanently delete your account and all associated data. This action cannot be undone.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <h3 className="font-semibold text-red-800 dark:text-red-200 mb-2">
                    What will be deleted:
                  </h3>
                  <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                    <li>• Your user profile and account information</li>
                    <li>• All posts, drafts, and content you've created</li>
                    <li>• All uploaded media files and assets</li>
                    <li>• AI task history and generated content</li>
                    <li>• Client profiles and associated data</li>
                    <li>• Team memberships and collaborations</li>
                    <li>• All analytics and usage data</li>
                  </ul>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Type "DELETE" to confirm account deletion:
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder="DELETE"
                    className="max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    style={{ 
                      color: '#111827',
                      backgroundColor: '#ffffff',
                      borderColor: '#d1d5db'
                    }}
                  />
                </div>
                
                <Button
                  variant="destructive"
                  onClick={deleteMyAccount}
                  disabled={loading || deleteConfirmation !== 'DELETE'}
                  className="flex items-center"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {loading ? 'Deleting Account...' : 'Delete My Account Permanently'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default GDPRCompliancePage;